import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import Card from 'components/Wizard/Card'
import SidebarTabs from 'components/Sidebar/SidebarTabs'
import TabCircleCount from 'components/Sidebar/SidebarTabs/components/TabCircleCount'
import AcceptSidebar from './components/AcceptSidebar'
import ReadyToApply from './components/ReadyToApply'
import Invited from './components/Invited'
import Applied from './components/Applied'
import Selected from './components/Selected'
import Rejected from './components/Rejected'
import CreateCardPlaceholder from 'components/CreateCardPlaceholder'
import { t } from 'helpers'
import { EventBus } from 'helpers/EventBus'
import useCastingRelation from 'hooks/api/useCastingRelation'
import { useAppStore, useCastingStore } from 'store'
import { CASTING_STATUS, DASHBOARD_SECTIONS } from 'types/enums'
import Waiting from './components/Waiting'

const tabs = [
	'APP_SUBMISSION_EXPIRE_LABEL',
	'APP_INVITED_LABEL',
	'APP_WAITING_LABEL',
	'APP_REJECTED_LABEL',
	'APP_READY_TO_APPLY_LABEL',
	'APP_CASTING_LABEL',
	'APP_SELECTED_LABEL',
	'APP_ARCHIVED_LABEL',
].map((label, index) => ({
	label: label,
	value: index,
}))

const ActorCasting = (): JSX.Element => {
	const { data: castingRelations, refresh } = useCastingRelation('/Lookup')
	const swiperRef = useRef<SwiperClass>()
	const [activeTab, setActiveTab] = useState(tabs?.[0].value)
	const [realCounts, setRealCounts] = useState<{ [key: number]: number }>({})
	const handleRealCountUpdate = (tabIndex: number, count: number) => {
		setRealCounts((prev) => ({ ...prev, [tabIndex]: count }))
	}
	const tagsCount = useCastingStore(useShallow((state) => state.tagsCount))
	const invited = castingRelations?.filter(
		(relation) => relation.status === CASTING_STATUS.INVITED,
	)

	const waiting = castingRelations?.filter((relation) =>
		[CASTING_STATUS.WAITING].includes(relation.status as CASTING_STATUS),
	)
	const readyToApply = castingRelations?.filter((relation) =>
		[
			CASTING_STATUS.READY,
			CASTING_STATUS.UPLOADING,
			CASTING_STATUS.REAPPLIED,
		].includes(relation.status as CASTING_STATUS),
	)
	const applied = castingRelations?.filter((relation) =>
		[
			CASTING_STATUS.APPLIED,
			CASTING_STATUS.FAVORITE,
			CASTING_STATUS.LIKED,
			CASTING_STATUS.NEUTRAL,
			CASTING_STATUS.DISLIKED,
			CASTING_STATUS.RESUBMITED,
		].includes(relation.status as CASTING_STATUS),
	)
	const selected = castingRelations?.filter((relation) =>
		[CASTING_STATUS.SELECTED].includes(relation.status as CASTING_STATUS),
	)
	const rejected = castingRelations?.filter((relation) =>
		[CASTING_STATUS.REJECTED].includes(relation.status as CASTING_STATUS),
	)

	const count: { [key: number]: string } = useMemo(() => {
		const defaultCounts = [
			tagsCount.expiring,
			invited?.length,
			waiting?.length,
			rejected?.length,
			readyToApply?.length,
			tagsCount.castingOpened,
			selected?.length,
			tagsCount.castingClosed,
		]

		return defaultCounts
			.map((c, index) => {
				const real = realCounts[index]
				const final = real !== undefined ? real : c

				return { [index]: final ? ` ${final}` : '' }
			})
			.reduce((prev, next) => ({ ...prev, ...next }), {})
	}, [tagsCount, invited, waiting, rejected, readyToApply, selected, realCounts])

	const [activeTodo, setActiveTodo] = useAppStore(
		useShallow((state) => [state.activeTodo, state.setActiveTodo]),
	)

	useEffect(() => {
		if (activeTodo && activeTodo.section === DASHBOARD_SECTIONS.CASTING) {
			setActiveTab(activeTodo.index)
			swiperRef.current?.slideTo(activeTodo.index)
			setActiveTodo(null)
		}
	}, [activeTodo, setActiveTodo])

	useEffect(() => {
		EventBus.$on('refreshActorBoard', () => {
			refresh()
		})

		return () => {
			EventBus.$off('refreshActorBoard')
		}
	}, [refresh])

	return (
		<>
			<AcceptSidebar />
			<Card step="actor_board" className="cardWrapperSize" noHint>
				<>
					<SidebarTabs
						scroll
						active={activeTab}
						options={tabs?.map((tab, index) => ({
							...tab,
							label: count[index] ? (
								<TabCircleCount label={tab.label} count={count[index]} />
							) : (
								t(tab.label)
							),
						}))}
						onChange={(v) => {
							setActiveTab(v as number)
							swiperRef.current?.slideTo(v as number)
						}}
						type="fit"
					/>
					<Swiper
						allowTouchMove={false}
						onSwiper={(swiper: SwiperClass) => {
							swiperRef.current = swiper
						}}
						style={{ width: '100%' }}>
						<SwiperSlide>
							{readyToApply?.length ? (
								<div style={activeTab !== 0 ? { position: 'absolute' } : {}}>
									<ReadyToApply relations={readyToApply} sortExpired />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
						<SwiperSlide>
							{invited?.length ? (
								<div style={activeTab !== 1 ? { position: 'absolute' } : {}}>
									<Invited relations={invited} InvitedCount = {realCounts[1]} onCountUpdate={(count) => handleRealCountUpdate(1, count)} />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
						<SwiperSlide>
							{waiting?.length ? (
								<div style={activeTab !== 2 ? { position: 'absolute' } : {}}>
									<Waiting relations={waiting} />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
						<SwiperSlide>
							{rejected?.length ? (
								<div style={activeTab !== 3 ? { position: 'absolute' } : {}}>
									<Rejected relations={rejected} />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
						<SwiperSlide>
							{readyToApply?.length ? (
								<div style={activeTab !== 4 ? { position: 'absolute' } : {}}>
									<ReadyToApply relations={readyToApply} />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
						<SwiperSlide>
							{applied?.length ? (
								<div style={activeTab !== 5 ? { position: 'absolute' } : {}}>
									<Applied relations={applied} filter="castingOpened" />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
						<SwiperSlide>
							{selected?.length ? (
								<div style={activeTab !== 6 ? { position: 'absolute' } : {}}>
									<Selected relations={selected} />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
						<SwiperSlide>
							{applied?.length ? (
								<div style={activeTab !== 7 ? { position: 'absolute' } : {}}>
									<Applied relations={applied} filter="castingClosed" />
								</div>
							) : (
								<div className="responsiveGrid">
									<CreateCardPlaceholder
										label="APP_RESULT_NOT_FOUND"
										style={{ height: '165px' }}
									/>
								</div>
							)}
						</SwiperSlide>
					</Swiper>
				</>
			</Card>
		</>
	)
}

export default ActorCasting
